import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle, ArrowRight, Sparkles } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import AppHeader from "../components/AppHeader";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", attempts: 0, data: null, error: "" });
  const pollRef = useRef(null);

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", error: "Sesión no encontrada", attempts: 0, data: null }); return; }
    let cancelled = false;
    const MAX = 20;
    const poll = async (n = 0) => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        if (data.payment_status === "paid") {
          setState({ status: "paid", data, attempts: n, error: "" });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", data, attempts: n, error: "" });
          return;
        }
        if (n >= MAX) {
          setState({ status: "timeout", data, attempts: n, error: "" });
          return;
        }
        setState({ status: "polling", attempts: n, data, error: "" });
        pollRef.current = setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        if (cancelled) return;
        setState({ status: "error", error: formatApiError(e.response?.data?.detail) || e.message, attempts: n, data: null });
      }
    };
    poll(0);
    return () => { cancelled = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader />
      <main className="max-w-3xl mx-auto px-6 py-16">
        {state.status === "polling" && (
          <div className="bg-white border border-stone-200 p-10 rounded-sm text-center" data-testid="payment-polling">
            <Loader2 className="w-12 h-12 text-[#E0651F] animate-spin mx-auto mb-4" />
            <h1 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">Confirmando tu pago…</h1>
            <p className="text-stone-600 text-sm">Estamos verificando con Stripe. Esto puede tardar unos segundos. Intento {state.attempts + 1} / 20.</p>
          </div>
        )}
        {state.status === "paid" && (
          <div className="bg-white border-l-4 border-emerald-500 border border-stone-200 p-10 rounded-sm" data-testid="payment-success">
            <CheckCircle2 className="w-14 h-14 text-emerald-500 mb-4" />
            <div className="text-[11px] tracking-[0.25em] uppercase font-bold text-emerald-700 mb-2">Pago confirmado</div>
            <h1 className="font-display text-4xl font-black tracking-tight text-[#2d1f1a] mb-4">¡Gracias por tu compra!</h1>
            <p className="text-stone-700 leading-relaxed mb-6">
              Hemos recibido tu pago de <strong>{state.data?.amount?.toFixed(2)} €</strong>. Tu acceso a la app ya está activo y te hemos enviado un email con la confirmación. La factura con tus datos fiscales llegará en breve.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/admin/generate" className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors" data-testid="go-generate-btn">
                <Sparkles className="w-4 h-4" /> Generar mi primer informe <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/admin" className="inline-flex items-center gap-2 bg-white hover:bg-stone-100 text-[#2d1f1a] border border-stone-300 px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
                Ir al panel
              </Link>
            </div>
          </div>
        )}
        {(state.status === "expired" || state.status === "timeout") && (
          <div className="bg-white border-l-4 border-amber-500 border border-stone-200 p-10 rounded-sm" data-testid="payment-pending">
            <AlertCircle className="w-12 h-12 text-amber-500 mb-4" />
            <h1 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">Estamos verificando el pago…</h1>
            <p className="text-stone-700 leading-relaxed mb-6">
              El proceso está tardando más de lo habitual. Si el cobro se ha realizado correctamente, tu acceso se activará en cuanto Stripe nos confirme la operación. Si en 10 minutos no recibes el email, contáctanos.
            </p>
            <Link to="/admin" className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm transition-colors">
              Ir al panel
            </Link>
          </div>
        )}
        {state.status === "error" && (
          <div className="bg-white border-l-4 border-red-500 border border-stone-200 p-10 rounded-sm" data-testid="payment-error">
            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
            <h1 className="font-display text-3xl font-black tracking-tight text-[#2d1f1a] mb-3">Algo no ha ido bien</h1>
            <p className="text-stone-700 mb-6">{state.error || "No hemos podido verificar el pago."}</p>
            <button onClick={() => navigate("/admin")} className="inline-flex items-center gap-2 bg-[#2d1f1a] hover:bg-[#E0651F] text-white px-6 py-3 text-xs tracking-[0.18em] uppercase font-bold rounded-sm">
              Volver al panel
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
